/**
 * @file useConsentTextsEditor.ts
 * @description Состояние формы новой редакции текстов согласия (phase19.16,
 * Р3г; uiux-r3g-consent-texts §3–§5): правка полей, ошибки (shared-проверка
 * + 422 сервера), предупреждения, «Отличия от ред. N», черновик в браузере,
 * публикация с разбором 409 и 422.
 *
 * Ошибки и отличия — производные от текстов (useMemo), а не отдельное
 * состояние. «Отличия от ред. N» — от основы; «кого коснётся» в окне
 * публикации — от действующей редакции `expectedCurrent` (ревью В1:
 * «Взять за основу» старую редакцию без правок всё равно меняет текст на сайтах). 409: свежая действующая редакция читается с сервера, правки
 * сохраняются черновиком — не теряются (uiux §5 «Конфликт»).
 */

import type { ConsentTextKind } from "@chaiberi/shared";
import { useCallback, useMemo, useState } from "react";

import {
  consentIssuesFromError,
  consentPublishErrorText,
  consentTextsApi,
  isConsentConflict,
  type ConsentTextRevision,
} from "../../lib/api-consent-texts";
import { clearConsentDraft, draftDiffers, saveConsentDraft } from "../../lib/platform/consent-texts-draft";
import {
  changedConsentFields,
  consentFieldErrors,
  issuesToFieldErrors,
  normalizeConsentTexts,
  setTextAt,
  type ConsentFieldErrors,
} from "../../lib/platform/consent-texts-form";
import { consentFieldWarnings } from "../../lib/platform/consent-texts-warnings";
import type { ConsentAnyTexts, ConsentEditStart } from "./useConsentTextsSource";

/** Кто опередил: свежая действующая редакция (null — не удалось прочитать). */
export interface ConsentConflict {
  revision: ConsentTextRevision | null;
}

export type ConsentPublishResult = { ok: true; number: number } | { ok: false; reason: "conflict" | "invalid" | "failed" };

export interface ConsentTextsEditor {
  texts: ConsentAnyTexts;
  base: ConsentTextRevision;
  expectedCurrent: number;
  errors: ConsentFieldErrors;
  warnings: ReadonlyMap<string, string>;
  changed: { field: string; label: string }[];
  /** Отличия от действующей редакции — что реально сменится на сайтах. */
  liveChanged: { field: string; label: string }[];
  /** Есть правки, не сохранённые черновиком. */
  dirty: boolean;
  draftSavedAt: string | null;
  conflict: ConsentConflict | null;
  publishing: boolean;
  publishError: string | null;
  setField: (path: string, value: string) => void;
  saveDraft: () => boolean;
  publish: (note: string) => Promise<ConsentPublishResult>;
  /** «Сравнить»: отличия — от вышедшей редакции, публикация — поверх неё. */
  compareWithConflict: () => void;
  /** «Начать заново от ред. N»: форма = вышедшая редакция (черновик остаётся). */
  restartFromConflict: () => void;
  clearPublishError: () => void;
}

export function useConsentTextsEditor(
  kind: ConsentTextKind,
  current: ConsentTextRevision,
  start: ConsentEditStart,
  authorName: string,
): ConsentTextsEditor {
  const [texts, setTexts] = useState<ConsentAnyTexts>(start.texts);
  const [savedTexts, setSavedTexts] = useState<ConsentAnyTexts>(start.texts);
  const [base, setBase] = useState(start.base);
  const [live, setLive] = useState(start.live);
  const [expectedCurrent, setExpectedCurrent] = useState(start.expectedCurrent);
  const [draftSavedAt, setDraftSavedAt] = useState(start.draftSavedAt);
  const [serverErrors, setServerErrors] = useState<ConsentFieldErrors>(new Map());
  // Черновик старше действующей редакции — конфликт виден сразу, до публикации.
  const [conflict, setConflict] = useState<ConsentConflict | null>(
    current.number !== start.expectedCurrent ? { revision: current } : null,
  );
  const [publishing, setPublishing] = useState(false);
  const [publishError, setPublishError] = useState<string | null>(null);

  const errors = useMemo<ConsentFieldErrors>(() => {
    const local = consentFieldErrors(kind, texts);
    if (serverErrors.size === 0) return local;
    return new Map([...serverErrors, ...local]);
  }, [kind, texts, serverErrors]);
  const warnings = useMemo(() => consentFieldWarnings(kind, texts), [kind, texts]);
  const changed = useMemo(() => changedConsentFields(kind, base.texts, texts), [kind, base, texts]);
  const liveChanged = useMemo(() => changedConsentFields(kind, live.texts, texts), [kind, live, texts]);
  const dirty = draftDiffers(kind, texts, savedTexts);

  const setField = useCallback((path: string, value: string) => {
    // Перевод строки в тексте баннера не нужен: Enter не ломает фразу.
    setTexts((prev) => setTextAt(prev, path, value.replace(/[\r\n]+/gu, " ")));
    setServerErrors((prev) => {
      if (!prev.has(path)) return prev;
      const next = new Map(prev);
      next.delete(path);
      return next;
    });
  }, []);

  const persist = useCallback(
    (value: ConsentAnyTexts, expected: number, baseNumber: number): boolean => {
      const savedAt = new Date().toISOString();
      const ok = saveConsentDraft({ kind, expectedCurrent: expected, baseNumber, texts: value, savedAt, authorName });
      if (ok) {
        setSavedTexts(value);
        setDraftSavedAt(savedAt);
      }
      return ok;
    },
    [kind, authorName],
  );

  const saveDraft = useCallback(() => persist(texts, expectedCurrent, base.number), [persist, texts, expectedCurrent, base]);

  const publish = useCallback(
    async (note: string): Promise<ConsentPublishResult> => {
      setPublishing(true);
      setPublishError(null);
      try {
        const payload = { expectedCurrent, texts: normalizeConsentTexts(kind, texts), note: note.trim() };
        const { number } = await consentTextsApi.publish(kind, payload);
        clearConsentDraft(kind);
        setSavedTexts(texts);
        return { ok: true, number };
      } catch (error) {
        if (isConsentConflict(error)) {
          persist(texts, expectedCurrent, base.number);
          const fresh = await consentTextsApi.current(kind).catch(() => null);
          setConflict({ revision: fresh });
          return { ok: false, reason: "conflict" };
        }
        const issues = consentIssuesFromError(error);
        if (issues.length > 0) {
          setServerErrors(issuesToFieldErrors(issues));
          return { ok: false, reason: "invalid" };
        }
        setPublishError(consentPublishErrorText(error));
        return { ok: false, reason: "failed" };
      } finally {
        setPublishing(false);
      }
    },
    [kind, texts, expectedCurrent, base, persist],
  );

  const compareWithConflict = useCallback(() => {
    const revision = conflict?.revision;
    if (!revision) return;
    setBase(revision);
    setLive(revision);
    setExpectedCurrent(revision.number);
    setConflict(null);
    // Черновик — уже от вышедшей редакции: иначе «Продолжить» снова покажет конфликт (ревью М4).
    persist(texts, revision.number, revision.number);
  }, [conflict, persist, texts]);

  const restartFromConflict = useCallback(() => {
    const revision = conflict?.revision;
    if (!revision) return;
    setTexts(revision.texts);
    setSavedTexts(revision.texts);
    setBase(revision);
    setLive(revision);
    setExpectedCurrent(revision.number);
    setServerErrors(new Map());
    setConflict(null);
  }, [conflict]);

  const clearPublishError = useCallback(() => setPublishError(null), []);

  return {
    texts,
    base,
    expectedCurrent,
    errors,
    warnings,
    changed,
    liveChanged,
    dirty,
    draftSavedAt,
    conflict,
    publishing,
    publishError,
    setField,
    saveDraft,
    publish,
    compareWithConflict,
    restartFromConflict,
    clearPublishError,
  };
}
